import Autoplay from 'embla-carousel-autoplay';
import { Newspaper } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

export type NewsCarouselItem = {
  id: string;
  title: string;
  body: string;
};

type Props = {
  items: NewsCarouselItem[];
  isLoading?: boolean;
};

/**
 * Renders the news carousel used on the webapp landing page. Mirrors the
 * Overwolf desktop news block so both surfaces read the same. Presentational
 * only — pass in pre-fetched news items and we render, autoplay, and paginate.
 *
 * Responsive notes:
 * - On mobile we drop the prev/next arrows and lean on swipe + dots; autoplay
 *   pauses on touch.
 * - The markdown body scrolls within a fixed-ish max height so a long post
 *   doesn't push the CTA off the page.
 */
export function NewsCarousel({ items, isLoading }: Props) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm shadow-sm">
      <div className="flex items-center gap-2 px-5 sm:px-6 pt-5 sm:pt-6">
        <Newspaper className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Mainstage News</h2>
      </div>

      <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-4">
        {isLoading ? (
          <div className="text-muted-foreground text-sm">Loading news…</div>
        ) : items.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            No news available at this time.
          </div>
        ) : (
          <Carousel
            opts={{ loop: true }}
            plugins={[
              Autoplay({
                delay: 12000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent>
              {items.map((item) => (
                <CarouselItem key={item.id}>
                  <NewsItemContent title={item.title} body={item.body} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {items.length > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50 gap-2">
                {/* Arrows are a nice-to-have on desktop; on phones the swipe
                    gesture is the primary affordance, so we hide them. */}
                <CarouselPrevious className="static translate-y-0 h-8 w-8 hidden sm:inline-flex" />
                <CarouselDots count={items.length} className="pt-0 flex-1" />
                <CarouselNext className="static translate-y-0 h-8 w-8 hidden sm:inline-flex" />
              </div>
            )}
          </Carousel>
        )}
      </div>
    </div>
  );
}

function NewsItemContent({ title, body }: { title: string; body: string }) {
  return (
    <div className="space-y-3">
      <h3 className="text-lg sm:text-xl font-semibold sticky top-0 bg-card/70 backdrop-blur-sm pb-2">
        {title}
      </h3>
      <div className="prose prose-sm dark:prose-invert max-w-none max-h-[320px] sm:max-h-[360px] overflow-y-auto pr-1 sm:pr-2">
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="text-xl font-bold mt-3 mb-2">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-lg font-semibold mt-2 mb-1">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="my-1.5 text-muted-foreground leading-relaxed">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc pl-5 my-1.5">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-5 my-1.5">{children}</ol>
            ),
            li: ({ children }) => (
              <li className="my-0.5 text-muted-foreground">{children}</li>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                className="text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">
                {children}
              </strong>
            ),
            em: ({ children }) => <em className="italic">{children}</em>,
            table: ({ children }) => (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse my-2 text-sm">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-muted/50">{children}</thead>
            ),
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => (
              <tr className="border-b border-border">{children}</tr>
            ),
            th: ({ children }) => (
              <th className="px-2 py-1.5 text-left font-semibold border border-border">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-2 py-1.5 border border-border">{children}</td>
            ),
          }}
        >
          {body}
        </Markdown>
      </div>
    </div>
  );
}
